import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { Button, Icon, Input, Modal, Popup } from 'semantic-ui-react';
import api from '../../../api';
import MenuHorizontal from '../../layouts/MenuHorizontal';
import MenuVertical from '../../layouts/MenuVertical';
import { connect, disconnect, socket } from '../../../socket';
import { useSelector } from 'react-redux';
import moment from 'moment';
import 'moment/locale/pt-br';

import './QuestionChat.css';

const iniciais = nome =>
  (nome || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');

const rotuloDia = data => {
  const d = moment(data);
  if (d.isSame(moment(), 'day')) return 'Hoje';
  if (d.isSame(moment().subtract(1, 'day'), 'day')) return 'Ontem';
  return d.format('DD [de] MMMM [de] YYYY');
};

const genHashChat = () => {
  const caracteres = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let resultado = '';
  for (let i = 0; i < 15; i++) resultado += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
  return resultado;
};

const QuestionChat = ({ match: { params } }) => {
  const history = useHistory();
  const usuario = useSelector(state => state.user.user);

  const [condominio, setCondominio] = useState({});
  const [pergunta, setPergunta] = useState(null);
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [nomeUsuario, setNomeUsuario] = useState(usuario.nome);
  const [hashUsuario, setHashUsuario] = useState(null);
  const [mensagem, setMensagem] = useState('');
  const [mensagens, setMensagens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const fimRef = useRef(null);
  const hashRef = useRef(null);

  useEffect(() => {
    connect();

    try {
      const chatData = window.localStorage.getItem('sindico-chat');
      if (chatData) {
        const chat = JSON.parse(chatData).find(it => it.pergunta === params.idPergunta);
        if (chat) {
          hashRef.current = chat.hash;
          setHashUsuario(chat.hash);
          setNomeUsuario(chat.nome);
        }
      }
    } catch (e) {
      // dados locais inválidos: o síndico só precisará informar o nome de novo
    }

    socket.on('messages', data => {
      setMensagens([...data].sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt))));
      setCarregando(false);
    });
    socket.on('message', nova => setMensagens(p => [...p, nova]));
    socket.on('moderated', atualizada =>
      setMensagens(p => p.map(it => (it._id === atualizada._id ? { ...it, moderado: atualizada.moderado } : it)))
    );

    socket.emit('joinChat', { pergunta: params.idPergunta });

    (async () => {
      try {
        setCondominio(await api.condominio.findSimpleById(params.condominioId));
        setPergunta(await api.pergunta.findById(params.idPergunta));
      } catch (e) {
        setErrors((e.data && e.data.errors) || {});
      }
    })();

    return () => {
      socket.off('messages');
      socket.off('message');
      socket.off('moderated');
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fimRef.current) fimRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [mensagens.length, busca]);

  const enviar = () => {
    const texto = mensagem.trim();
    if (!texto) return;
    socket.emit('sendMessageSindico', {
      idSindico: usuario._id,
      pergunta: params.idPergunta,
      nomeUsuario,
      hashUsuario,
      mensagem: texto,
    });
    setMensagem('');
  };

  const onKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const confirmarNome = () => {
    if (!nomeUsuario.trim()) return;
    let data = [];
    try {
      data = JSON.parse(window.localStorage.getItem('sindico-chat') || '[]');
    } catch (e) {
      data = [];
    }
    if (hashUsuario) {
      data = data.map(it => (it.pergunta === params.idPergunta && it.hash === hashUsuario ? { ...it, nome: nomeUsuario } : it));
    } else {
      const hash = genHashChat();
      data.push({ pergunta: params.idPergunta, nome: nomeUsuario, hash });
      hashRef.current = hash;
      setHashUsuario(hash);
    }
    window.localStorage.setItem('sindico-chat', JSON.stringify(data));
    setModalOpen(false);
  };

  const moderar = msg => socket.emit('moderateMessage', { _id: msg._id, moderado: msg.moderado });

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return mensagens;
    return mensagens.filter(m =>
      String(m.text || '').toLowerCase().includes(termo) || String((m.user && m.user.nome) || '').toLowerCase().includes(termo));
  }, [mensagens, busca]);

  const participantes = useMemo(
    () => new Set(mensagens.map(m => (m.user && (m.user._id || m.user.hash || m.user.nome)) || '')).size,
    [mensagens]
  );

  let diaAnterior = null;

  return (
    <>
      {!!errors.global && <Redirect to="/" />}
      <MenuHorizontal />
      <div className="corpo-dashboard-mini">
        <MenuVertical condominio={condominio} />
        <div className="va-chat">
          <header className="va-chat-topo">
            <Button className="va-btn-acao va-btn-editar" icon onClick={() => history.goBack()} aria-label="Voltar">
              <Icon name="arrow left" />
            </Button>
            <div className="va-chat-titulo">
              <small>Chat da pauta</small>
              <h2>{pergunta ? pergunta.titulo : 'Carregando...'}</h2>
            </div>
            <div className="va-chat-resumo">
              <span><Icon name="comments outline" /> {mensagens.length}</span>
              <span><Icon name="user outline" /> {participantes}</span>
            </div>
          </header>

          <div className="va-chat-busca">
            <Icon name="search" />
            <input placeholder="Buscar por mensagem ou autor..." value={busca} onChange={e => setBusca(e.target.value)} />
            {busca && <button type="button" onClick={() => setBusca('')} aria-label="Limpar busca"><Icon name="close" /></button>}
          </div>

          <div className="va-chat-lista" role="log" aria-live="polite">
            {carregando && <div className="va-chat-vazio"><Icon loading name="spinner" /> Carregando mensagens...</div>}
            {!carregando && visiveis.length === 0 && (
              <div className="va-chat-vazio">
                <Icon name="comments outline" size="big" />
                <p>{busca ? 'Nenhuma mensagem encontrada.' : 'Ainda não há mensagens nesta pauta.'}</p>
              </div>
            )}
            {visiveis.map(m => {
              const nome = (m.user && m.user.nome) || '-';
              const sindico = !!(m.user && m.user.isSindico);
              const minha = sindico && !!m.user.hash && m.user.hash === (hashUsuario || hashRef.current);
              const dia = moment(m.createdAt).format('YYYY-MM-DD');
              const separador = dia !== diaAnterior;
              diaAnterior = dia;
              const unidade = m.unidade ? [m.unidade.bloco, m.unidade.numero].filter(Boolean).join(' · ') : '';
              return (
                <React.Fragment key={m._id || `${dia}-${m.createdAt}-${nome}`}>
                  {separador && <div className="va-chat-dia"><span>{rotuloDia(m.createdAt)}</span></div>}
                  <div className={`va-chat-msg ${minha ? 'minha' : ''} ${sindico ? 'sindico' : ''} ${m.moderado ? 'oculta' : ''}`}>
                    <span className="va-chat-avatar">{iniciais(nome)}</span>
                    <div className="va-chat-balao">
                      <div className="va-chat-cabecalho">
                        <b>{nome}</b>
                        {sindico && <em className="va-chat-selo">Síndico</em>}
                        {unidade && <em className="va-chat-unidade">Unidade {unidade}</em>}
                        <time>{moment(m.createdAt).format('HH:mm')}</time>
                      </div>
                      <p>{m.moderado ? 'Mensagem ocultada pela moderação' : m.text}</p>
                      {m._id && !sindico && (
                        <Popup
                          content={m.moderado ? 'Exibir novamente para todos' : 'Ocultar para todos os participantes'}
                          position="top center"
                          trigger={
                            <button type="button" className="va-chat-moderar" onClick={() => moderar(m)}>
                              <Icon name={m.moderado ? 'eye' : 'eye slash outline'} /> {m.moderado ? 'Exibir' : 'Ocultar'}
                            </button>
                          }
                        />
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={fimRef} />
          </div>

          <footer className="va-chat-rodape">
            {hashUsuario ? (
              <>
                <div className="va-chat-autor">
                  <span className="va-chat-avatar pequeno">{iniciais(nomeUsuario)}</span>
                  <span>Você participa como <b>{nomeUsuario}</b></span>
                  <button type="button" onClick={() => setModalOpen(true)}>Alterar nome</button>
                </div>
                <div className="va-chat-entrada">
                  <textarea
                    rows={1}
                    placeholder="Escreva uma mensagem... (Enter envia, Shift+Enter quebra a linha)"
                    value={mensagem}
                    onChange={e => setMensagem(e.target.value)}
                    onKeyDown={onKeyDown}
                    maxLength={1000}
                  />
                  <Button className="btn-ui-1" icon labelPosition="right" onClick={enviar} disabled={!mensagem.trim()}>
                    Enviar <Icon name="send" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="va-chat-entrar">
                <span>Você está apenas acompanhando. Entre no chat para responder aos participantes.</span>
                <Button className="btn-ui-1" onClick={() => setModalOpen(true)}><Icon name="comment" /> Entrar no chat</Button>
              </div>
            )}
          </footer>
        </div>
      </div>

      <Modal
        size="tiny"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        header="Como você quer aparecer no chat?"
        content={
          <div className="input-nome-sindico">
            <Input
              name="nomeUsuario"
              type="text"
              placeholder="Informe o nome exibido nas mensagens"
              value={nomeUsuario}
              onChange={e => setNomeUsuario(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmarNome()}
              autoFocus
              fluid
            />
          </div>
        }
        actions={[
          { key: 'cancel', onClick: () => setModalOpen(false), content: 'Cancelar', className: 'btn-ui-1 btn-ui-1-basic' },
          { key: 'confirm', onClick: confirmarNome, content: hashUsuario ? 'Salvar' : 'Entrar no chat', className: 'btn-ui-1' },
        ]}
      />
    </>
  );
};

export default QuestionChat;
